import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

export async function getLearningUploadContext(contextId) {
  const response = await axios.get(
    `${API}/api/training/proof/upload-context/${encodeURIComponent(contextId)}`, OPTS,
  );
  return response.data.context;
}

export async function resolveExternalMediaRoutingContext(capturedAt) {
  const response = await axios.post(
    `${API}/api/media/external/routing-context/resolve`,
    { captured_at: capturedAt },
    OPTS,
  );
  return response.data;
}

export async function createExternalMediaJob({
  ndgMediaId,
  file,
  totalParts,
  purposeCode,
  fingerprint,
  sourceDevice = "browser",
  routingContext = null,
  learningContextId = null,
  signal,
}) {
  let effectiveRoutingContext = routingContext;

  // Lesson-aware callers may pass governed context explicitly. The bulk
  // Import Today's Recordings flow does not know lesson identity itself, so
  // resolve File.lastModified against the canonical lesson calendar here.
  // Resolution is fail-closed: zero/ambiguous matches simply leave the job
  // unlinked rather than guessing a pupil or lesson.
  if (!learningContextId && !effectiveRoutingContext && file?.lastModified) {
    const capturedAt = new Date(file.lastModified).toISOString();
    const resolved = await resolveExternalMediaRoutingContext(capturedAt);
    if (resolved?.matched && resolved?.routing_context) {
      effectiveRoutingContext = resolved.routing_context;
    }
  }

  const response = await axios.post(
    `${API}/api/media/external/upload-jobs`,
    {
      ndg_media_id: ndgMediaId,
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      byte_size: file.size,
      total_parts: totalParts,
      purpose_code: purposeCode,
      client_file_fingerprint: fingerprint,
      source_device: sourceDevice,
      ...(learningContextId ? { learning_context_id: learningContextId } : {}),
      ...(effectiveRoutingContext ? { routing_context: effectiveRoutingContext } : {}),
    },
    { ...OPTS, timeout: 20000, signal },
  );
  return response.data;
}

export async function presignExternalMediaPart(ndgMediaId, partNumber, { signal } = {}) {
  const response = await axios.post(
    `${API}/api/media/external/upload-jobs/${encodeURIComponent(ndgMediaId)}/parts/${partNumber}/presign`,
    {},
    { ...OPTS, timeout: 20000, signal },
  );
  return response.data;
}

export async function finalizeExternalMediaJob(ndgMediaId, { signal } = {}) {
  const response = await axios.post(
    `${API}/api/media/external/upload-jobs/${encodeURIComponent(ndgMediaId)}/finalize`,
    {},
    { ...OPTS, timeout: 20000, signal },
  );
  return response.data;
}

export async function putPartDirectToR2(uploadUrl, blob, onProgress, { signal } = {}) {
  await axios.put(uploadUrl, blob, {
    // Deliberately no NDG cookies or Authorization header. This request goes
    // directly from the browser/device to the short-lived signed R2 URL.
    withCredentials: false,
    timeout: 60000,
    signal,
    transformRequest: [(data) => data],
    onUploadProgress: onProgress,
  });
}

export async function requestExternalMediaReview(mediaId) {
  const { data } = await axios.post(`${API}/api/media/external/upload-jobs/${encodeURIComponent(mediaId)}/reviews`, {}, { ...OPTS, timeout: 20000 });
  return data;
}

export async function readExternalMediaReview(mediaId, reviewId) {
  const { data } = await axios.get(`${API}/api/media/external/upload-jobs/${encodeURIComponent(mediaId)}/reviews/${encodeURIComponent(reviewId)}`, { ...OPTS, timeout: 20000 });
  return data;
}

export async function prepareExternalReviewEvidence(mediaId, reviewId) {
  const { data } = await axios.post(`${API}/api/media/external/upload-jobs/${encodeURIComponent(mediaId)}/reviews/${encodeURIComponent(reviewId)}/evidence`, {}, { ...OPTS, timeout: 20000 });
  return data;
}

export async function reconcileExternalReviewFailure(mediaId, reviewId) {
  const { data } = await axios.post(`${API}/api/media/external/upload-jobs/${encodeURIComponent(mediaId)}/reviews/${encodeURIComponent(reviewId)}/reconcile-failure`, {}, { ...OPTS, timeout: 20000 });
  return data;
}
